import type { CSSProperties, JSX } from 'react';
import { styled } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';

export type ThumbnailStoryboardViewerProps = {
  storyboardUrl: string;
  storyboardHeight: number;
  storyboardWidth: number;
  frameWidth: number;
  frameHeight: number;
  frameCount: number;
  percent: number;
  targetWidth?: number;
  targetHeight?: number;
  targetAspectRatio?: number;
  scaleFactor?: number;
  framesPerRow?: number;
  cursorPosition?: number | null;
};

// this is the element that appears underneath the cursor when scrubbing
const ScrubLine = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  width: 2px;
  background-color: var(--wui-color-bg-fill);
  z-index: 1;
`;

// scaled dimensions based on aspect ratios
const getScaledDimensions = (
  originalAspectRatio: number,
  targetAspectRatio: number,
  viewerWidth: number,
  viewerHeight: number,
) => {
  let scaledHeight = viewerHeight;
  let scaledWidth = viewerWidth;

  if (targetAspectRatio > originalAspectRatio) {
    scaledHeight = viewerWidth / originalAspectRatio;
  } else {
    scaledWidth = viewerHeight * originalAspectRatio;
  }

  return { scaledHeight, scaledWidth };
};

// the position to overlay the image
const getCoverProps = (
  viewerWidth: number,
  viewerHeight: number,
  scaledWidth: number,
  scaledHeight: number,
) => {
  const top = (viewerHeight - scaledHeight) / 2;
  const left = (viewerWidth - scaledWidth) / 2;

  return {
    width: scaledWidth,
    height: scaledHeight,
    top,
    left,
  };
};

// the position for a specific frame
const getFramePosition = (
  frameIndex: number,
  framesPerRow: number,
  width: number,
  height: number,
  scaleFactor: number,
  frameCount: number,
  targetHeight: number,
) => {
  const col = frameIndex % framesPerRow;
  const row = Math.floor(frameIndex / framesPerRow);
  const imageX = -width * col * scaleFactor;
  const imageY = -height * row * scaleFactor;

  // Calculate vertical centering offset
  // The frame should be centered within the target height
  const rows = Math.ceil(frameCount / framesPerRow);
  const frameHeightInStoryboard = height / rows;
  const verticalCenterOffset = height > width ? (targetHeight - frameHeightInStoryboard) / 2 : 0;

  const adjustedY = imageY - verticalCenterOffset;

  return `${imageX}px ${adjustedY}px`;
};

export const ThumbnailStoryboardViewer = ({
  storyboardUrl,
  storyboardHeight,
  storyboardWidth,
  frameWidth,
  frameHeight,
  frameCount,
  percent,
  targetWidth = 200,
  targetHeight,
  targetAspectRatio,
  scaleFactor = 1,
  framesPerRow = 10,
  cursorPosition,
  ...props
}: ThumbnailStoryboardViewerProps): JSX.Element => {
  // dimensions based on aspect ratios
  const originalAspectRatio = frameWidth / frameHeight;
  const effectiveTargetAspectRatio = targetAspectRatio ?? originalAspectRatio;
  const calculatedHeight = targetWidth / effectiveTargetAspectRatio;
  const finalHeight = targetHeight ?? calculatedHeight;
  const viewerDimensions = {
    viewerWidth: targetWidth,
    viewerHeight: finalHeight,
  };

  // scaled dimensions to maintain aspect ratio
  const { scaledHeight, scaledWidth } = getScaledDimensions(
    originalAspectRatio,
    effectiveTargetAspectRatio,
    viewerDimensions.viewerWidth,
    viewerDimensions.viewerHeight,
  );

  // positioning to cover the viewer with the image
  const coverProps = getCoverProps(
    viewerDimensions.viewerWidth,
    viewerDimensions.viewerHeight,
    scaledWidth,
    scaledHeight,
  );

  // which frame to show based on percent
  const frameIndex = Math.floor(percent * frameCount);

  // the background position to show the correct frame
  const backgroundPosition = getFramePosition(
    frameIndex,
    framesPerRow,
    coverProps.width,
    coverProps.height,
    scaleFactor,
    frameCount,
    calculatedHeight,
  );

  // background size
  const scaleFactorFromAspectRatioFit = coverProps.height / frameHeight;
  const scaledStoryboardWidth = storyboardWidth * scaleFactorFromAspectRatioFit * scaleFactor;
  const scaledStoryboardHeight = Math.floor(
    storyboardHeight * scaleFactorFromAspectRatioFit * scaleFactor,
  );
  const backgroundSize = `${scaledStoryboardWidth}px ${scaledStoryboardHeight}px`;
  const backgroundImage = `url(${storyboardUrl})`;

  // only show the scrub line if the cursor position is available
  const showScrubLine = isNotNil(cursorPosition);

  // subtract 2px (scrubline width) to center the line under the cursor pointer
  const scrubLinePosition = isNotNil(cursorPosition) ? `${cursorPosition - 1}px` : '0';

  const viewerStyles: CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
    pointerEvents: 'none',
    width: `${viewerDimensions.viewerWidth * scaleFactor}px`,
    height: `${viewerDimensions.viewerHeight * scaleFactor}px`,
  };

  const storyboardStyles: CSSProperties = {
    position: 'absolute',
    width: coverProps.width,
    height: coverProps.height,
    left: coverProps.left,
    top: coverProps.top,
    backgroundImage,
    backgroundPosition,
    backgroundSize,
  };

  return (
    <div
      {...props}
      style={viewerStyles}
    >
      <div style={storyboardStyles} />
      {showScrubLine ? (
        <ScrubLine
          style={{
            left: scrubLinePosition,
          }}
        />
      ) : null}
    </div>
  );
};
